import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  type ActivityEntryDto,
  isFreshRequest,
  type ProposalDeadlineRadarItem,
  requestQueue,
  summarizeDeadlineRadar,
  type TripDto,
} from "@tc/shared";
import { Button, Card, CardHead, CardTitle, Icon, type IconName, Tag } from "@/components/ui";
import { AssignOwnerModal } from "@/features/ops/AssignOwnerModal";
import { opsApi } from "@/features/ops/api";
import { useOpsWorklist } from "@/features/ops/useOpsWorklist";
import { dateTone, STATUS_KEY, TONE_BG, TONE_COLOR } from "@/features/trips/status";
import { TransportIcons } from "@/features/trips/TripCard";
import { Can, useAuthz } from "@/lib/auth/authz";
import { useClubLookup } from "@/lib/clubs/clubs";
import { useFormatters } from "@/lib/i18n/format";

/**
 * The TC operator's landing experience (TW-3440): the back-office counterpart to the club
 * dashboard. Cross-club KPIs, the request queue, a deadline radar and an activity feed — all
 * derived from the cross-club trip list (`GET /api/trips`, RLS-bypassed for back office) the
 * same way the club dashboard folds its own club's list. Scoped by the topbar club switcher
 * (ClubFilterProvider). RoleLayout only mounts this for back-office users.
 */
export function OpsDashboard() {
  const { t } = useTranslation();
  const { me } = useAuthz();
  const { trips, owners, radar, loading, error, reload } = useOpsWorklist();
  const firstName = (me?.name ?? "").split(" ")[0] ?? "";

  return (
    <div className="page">
      <div className="row-between page-head">
        <div>
          <h1>
            {t("ops.hello")}
            {firstName && `, ${firstName}`}
          </h1>
          <div className="sub">{t("ops.sub")}</div>
        </div>
      </div>

      {loading && <div className="empty">{t("common.loading")}</div>}
      {error && <div className="empty">{t("ops.loadError")}</div>}
      {!loading && !error && <OpsBody trips={trips} owners={owners} radar={radar} reload={reload} />}
    </div>
  );
}

function OpsBody({
  trips,
  owners,
  radar,
  reload,
}: {
  trips: TripDto[];
  owners: ReturnType<typeof useOpsWorklist>["owners"];
  radar: ProposalDeadlineRadarItem[];
  reload: () => Promise<void>;
}) {
  const now = new Date().toISOString();
  const queue = requestQueue(trips);
  // "At risk" is now backed by real proposal deadlines (TW-3446), not the travel-date stand-in.
  const deadlines = summarizeDeadlineRadar(radar, now);

  return (
    <>
      <div className="dash-grid">
        <div className="stack">
          <RequestQueue queue={queue} owners={owners} reload={reload} />
        </div>
        <div className="stack">
          <DeadlineRadar deadlines={deadlines} />
          <ActivityFeed />
        </div>
      </div>
    </>
  );
}

/** The cross-club request queue: enquiries awaiting a proposal, longest-waiting first. */
function RequestQueue({
  queue,
  owners,
  reload,
}: {
  queue: TripDto[];
  owners: ReturnType<typeof useOpsWorklist>["owners"];
  reload: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const fmt = useFormatters();
  const navigate = useNavigate();
  const clubs = useClubLookup();
  const [assigning, setAssigning] = useState<TripDto | null>(null);

  return (
    <Card>
      <CardHead>
        <div>
          <CardTitle>{t("ops.queueTitle")}</CardTitle>
          <div className="faint" style={{ fontSize: 12.5, marginTop: 2 }}>
            {t("ops.queueSub")}
          </div>
        </div>
        <Tag variant="primary">{queue.length}</Tag>
      </CardHead>

      {queue.length === 0 ? (
        <div className="empty" style={{ padding: "30px 0" }}>
          {t("ops.noRequests")}
        </div>
      ) : (
        <div className="stack" style={{ gap: 12 }}>
          {queue.map((trip) => {
            const fresh = isFreshRequest(trip);
            const dest = [trip.destinationCity, trip.destinationCountry ? fmt.country(trip.destinationCountry) : ""].filter(Boolean).join(", ");
            return (
              <div key={trip.id} className={`queue-item${fresh ? " fresh" : ""}`}>
                <div className="queue-body">
                  <div className="queue-top">
                    <span className="t-id mono">{trip.reference}</span>
                    {fresh && (
                      <Tag shape="choice" choice={1}>
                        {t("ops.newLabel")}
                      </Tag>
                    )}
                  </div>
                  <div className="queue-match">{trip.matchOpponent ?? trip.sport ?? trip.reference}</div>
                  <div className="queue-meta">
                    <span className="mi">
                      <span className="club-dot" style={{ background: clubs.color(trip.clubId) }} />
                      {clubs.name(trip.clubId) ?? "—"}
                    </span>
                    {dest && (
                      <span className="mi">
                        <Icon name="building" size={13} />
                        {dest}
                      </span>
                    )}
                    {trip.travelStartDate && (
                      <span className="mi">
                        <Icon name="calendar" size={13} />
                        {fmt.date(trip.travelStartDate)}
                      </span>
                    )}
                    {trip.paxCount != null && (
                      <span className="mi">
                        <Icon name="roster" size={13} />
                        {trip.paxCount} {t("units.pax")}
                      </span>
                    )}
                    <span className="mi">
                      <TransportIcons modes={trip.transportModes} />
                    </span>
                  </div>
                </div>
                <div className="queue-side">
                  <span className="faint" style={{ fontSize: 11.5 }}>
                    {t("ops.waitingSince")} {fmt.relative(trip.createdAt)}
                  </span>
                  <div style={{ display: "flex", gap: 8 }}>
                    <Can permission="trip.write">
                      <Button variant="soft" size="sm" icon="user" onClick={() => setAssigning(trip)}>
                        {t("worklist.assign")}
                      </Button>
                    </Can>
                    <Button
                      variant="primary"
                      size="sm"
                      iconRight="arrowR"
                      onClick={() => navigate(`/trips/${trip.id}`)}
                    >
                      {t("ops.buildProposal")}
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {assigning && (
        <AssignOwnerModal
          trip={assigning}
          owners={owners}
          onClose={() => setAssigning(null)}
          onSaved={async () => {
            setAssigning(null);
            await reload();
          }}
        />
      )}
    </Card>
  );
}

/**
 * Deadline radar (TW-3446): the real, still-pending proposal deadlines across every club, soonest
 * first. Backed by `GET /api/proposals/deadline-radar` (one row per live, unmet deadline) — the same
 * `isDeadlineReminderPending` predicate that drives the reminder jobs — so the radar shows exactly
 * what would still nudge the club. Replaces the TW-3440 travel-date scaffold. Each row's tone keys
 * off the deadline date (overdue → danger), and clicking opens the trip.
 */
function DeadlineRadar({ deadlines }: { deadlines: ReturnType<typeof summarizeDeadlineRadar> }) {
  const { t } = useTranslation();
  const fmt = useFormatters();
  const navigate = useNavigate();
  const clubs = useClubLookup();
  const rows = deadlines.items.slice(0, 6);

  return (
    <Card>
      <CardHead>
        <div>
          <CardTitle>{t("ops.radarTitle")}</CardTitle>
          <div className="faint" style={{ fontSize: 12.5, marginTop: 2 }}>
            {t("ops.radarSub")}
          </div>
        </div>
        <Icon name="clock" size={18} className="faint" />
      </CardHead>
      {rows.length === 0 ? (
        <div className="empty" style={{ padding: "20px 0" }}>
          {t("ops.noDeadlines")}
        </div>
      ) : (
        rows.map((item) => {
          const tone = dateTone(item.date);
          return (
            <div
              className="deadline-row"
              key={`${item.proposalId}:${item.deadlineKey}`}
              onClick={() => navigate(`/trips/${item.tripId}`)}
              style={{ cursor: "pointer" }}
            >
              <span className="dl-ic" style={{ background: TONE_BG[tone], color: TONE_COLOR[tone] }}>
                <Icon name="clock" size={16} />
              </span>
              <div className="dl-body">
                <div className="dl-title">{item.label ?? (item.labelKey ? t(item.labelKey) : "")}</div>
                <div className="dl-meta">
                  <span
                    className="club-dot"
                    style={{ background: clubs.color(item.clubId), display: "inline-block", marginRight: 5 }}
                  />
                  {item.clubName ?? clubs.name(item.clubId) ?? item.tripReference ?? "—"}
                  {item.tripReference && ` · ${item.tripReference}`}
                </div>
              </div>
              <div className="dl-when" style={{ color: TONE_COLOR[tone] }}>
                {fmt.relative(item.date)}
                <small>{fmt.date(item.date)}</small>
              </div>
            </div>
          );
        })
      )}
    </Card>
  );
}

const ACTIVITY_ICON: Record<string, IconName> = {
  status_change: "send",
  owner_change: "user",
};

/** Recent cross-club activity, read from the audit log (GET /api/activity, back office only). */
function ActivityFeed() {
  const { t } = useTranslation();
  const fmt = useFormatters();
  const clubs = useClubLookup();
  const [entries, setEntries] = useState<ActivityEntryDto[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    opsApi
      .activity()
      .then((rows) => {
        if (!cancelled) setEntries(rows);
      })
      .catch(() => {
        if (!cancelled) setEntries([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  function lineFor(a: ActivityEntryDto): string {
    const reference = a.tripReference ?? "";
    if (a.action === "status_change" && a.status) {
      return t("activity.statusChanged", { reference, status: t(STATUS_KEY[a.status]) });
    }
    if (a.action === "owner_change") {
      return t("activity.ownerChanged", { reference });
    }
    return t("activity.updated", { reference });
  }

  return (
    <Card>
      <div className="card-title" style={{ marginBottom: 8 }}>
        {t("ops.activityTitle")}
      </div>
      {entries === null ? (
        <div className="empty" style={{ padding: "16px 0" }}>
          {t("common.loading")}
        </div>
      ) : entries.length === 0 ? (
        <div className="empty" style={{ padding: "16px 0" }}>
          {t("ops.noActivity")}
        </div>
      ) : (
        entries.map((a) => {
          const color = clubs.color(a.clubId);
          return (
            <div className="activity-row" key={a.id}>
              <span
                className="activity-ic"
                style={{ background: `color-mix(in oklab, ${color} 14%, white)`, color }}
              >
                <Icon name={ACTIVITY_ICON[a.action] ?? "clock"} size={15} />
              </span>
              <div style={{ flex: 1 }}>
                <div className="activity-text">{lineFor(a)}</div>
                <div className="activity-time">
                  {(a.clubName ?? clubs.name(a.clubId)) ?? "—"} · {fmt.relative(a.createdAt)}
                </div>
              </div>
            </div>
          );
        })
      )}
    </Card>
  );
}
